import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import MindfullLogo from '@/components/MindfullLogo';
import { MindfullMark } from '@/components/brand/MindfullMark';

const MIN_PASSWORD_LENGTH = 8;

// Read before the Supabase client consumes and clears the URL hash. An expired
// or reused recovery link comes back as #error=...&error_description=...
const initialHashParams = new URLSearchParams(
  typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : ''
);

/** The recovery link signs the user in (PASSWORD_RECOVERY); this page sets the new password. */
const ResetPassword = () => {
  const { session, isLoading, updatePassword } = useAuth();
  const navigate = useNavigate();

  const [linkError] = useState(() => initialHashParams.get('error_description'));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  // Recovery links verify asynchronously; wait for that before saying the link is bad.
  const [isVerifyingLink, setIsVerifyingLink] = useState(
    () => initialHashParams.get('type') === 'recovery' || new URLSearchParams(window.location.search).has('code')
  );

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setIsVerifyingLink(false);
      }
    });
    const timeout = window.setTimeout(() => setIsVerifyingLink(false), 5000);
    return () => {
      subscription.unsubscribe();
      window.clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSaving(true);
    const { error: updateError } = await updatePassword(password);
    setIsSaving(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    toast.success('Password updated');
    navigate('/canvas');
  };

  const isWaiting = isLoading || (isVerifyingLink && !session);
  const canSetPassword = !!session;

  return (
    <div className="min-h-screen bg-paper p-4 sm:p-6 lg:p-8">
      <div className="bg-paper-raised rounded-[2rem] sm:rounded-[3rem] min-h-[calc(100vh-2rem)] sm:min-h-[calc(100vh-3rem)] lg:min-h-[calc(100vh-4rem)] shadow-xl overflow-hidden flex flex-col">
        <header className="p-6 sm:px-10 lg:px-16">
          <Link to="/" className="inline-flex">
            <MindfullLogo size="md" />
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center px-4 pb-20">
          <Card className="w-full max-w-md shadow-2xl border-border/50 rounded-3xl">
            <CardHeader className="text-center space-y-4 pb-2">
              <div className="mx-auto w-16 h-16 bg-paper-raised rounded-2xl flex items-center justify-center shadow-lg border border-border/30 text-ink">
                <MindfullMark size={34} motion="intro" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">
                  {canSetPassword || isWaiting ? 'Set a new password' : 'Link expired'}
                </CardTitle>
                <CardDescription className="text-base mt-2">
                  {canSetPassword || isWaiting
                    ? 'Choose a new password for your Mindfull account.'
                    : linkError || 'This reset link is invalid or has expired. Request a new one.'}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-6 pb-8 px-8">
              {isWaiting ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-8 w-8 animate-spin text-ink" />
                </div>
              ) : canSetPassword ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isSaving}
                      className="rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isSaving}
                      className="rounded-xl"
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-12 text-base font-medium rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      'Update password'
                    )}
                  </Button>
                </form>
              ) : (
                <Button
                  asChild
                  className="w-full h-12 text-base font-medium rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Link to="/login?access=true">Back to sign in</Link>
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
